/**
 * glanceView — maps normalized MarketsData + GlanceMap → GlanceViewModel.
 *
 * CLIENT-SIDE (portable into the extension bundle — ADR-002 §5). No imports
 * from server-only modules. Pure; returns a new object.
 *
 * Glance grammar for markets.quote (documented + tested):
 *
 *   primary  : controlled by params.display (from GlanceMap config):
 *              - display='price' → "$182" (currency + integer price, ≤8 chars)
 *                For prices ≥ $1000: "$1,234" is 6 chars — fine.
 *                For prices ≥ $10,000: "$10,23" would be 7 chars — still fine.
 *                We use integer prices (no cents in primary) to stay within 8 chars.
 *                The secondary view (delta) carries the change/pct.
 *              - display='change' → "▲2.3%" or "▼1.2%" (direction + pct, ≤8 chars)
 *                Max chars: "▼100.0%" = 8 chars — at the exact limit.
 *                We show pct by default for change display (more universal than abs $).
 *
 *   delta    : context string that complements primary:
 *              - display='price' → "▲ $1.23 (0.7%)" or "▼ $2.34 (1.3%)" — change context
 *                BUT this can exceed 20 chars. We use: "▲$1.23 +0.7%" (13 chars max typical).
 *                Design decision: delta = "glyph$change pct%" — e.g., "▲$1.23 +0.7%"
 *              - display='change' → "$182.40" or "$182.40 prev $180" — the actual price
 *                Design decision: delta = price formatted as "$182.40" for change display
 *              Null when glance.delta is null.
 *
 *   state    : from direction field:
 *              - 'up'   → 'positive' (green accent)
 *              - 'down' → 'negative' (red accent)
 *              - 'flat' → 'neutral' (no accent — zero change / market closed)
 *              glance.state null → always 'neutral'.
 *
 * Edge-case rulings:
 *   1. change=0, direction='flat' → 'neutral' state, "→" glyph (not up/down).
 *   2. prevClose null → delta may omit comparison; works without it.
 *   3. Very large price (e.g. BRK.A ~$600k) → "$600k" would exceed 8 chars.
 *      Design: integer dollar, no comma/k shorthand — we trust the symbol basket
 *      doesn't include BRK.A for v1 (user picks common tickers). The central
 *      clamp in viewModelFromResponse is the last line of defence.
 *   4. Negative price (short data anomaly) → direction='down', state='negative'.
 */

import type { GlanceMap } from 'briefpane/schema';
import type { GlanceState, GlanceViewModel, DirectionGlyph } from 'briefpane/contracts';
import { DIRECTION_GLYPHS } from 'briefpane/contracts';
import type { MarketsData } from './normalize.js';

/** Map MarketsData direction → GlanceState. */
function directionToState(direction: MarketsData['direction']): GlanceState {
  if (direction === 'up') return 'positive';
  if (direction === 'down') return 'negative';
  return 'neutral'; // flat — zero change / market closed
}

/** Format a price as a dollar string with 2 decimal places, e.g. "$182.40". */
function formatPrice(price: number): string {
  return `$${Math.abs(price).toFixed(2)}`;
}

/** Format an integer dollar price for the primary slot: "$182" — no cents. */
function formatPrimaryPrice(price: number): string {
  return `$${Math.round(Math.abs(price))}`;
}

/** Format a percent change for display, e.g. "+0.7%" or "-1.2%". */
function formatPct(pct: number): string {
  const sign = pct >= 0 ? '+' : '-';
  return `${sign}${Math.abs(pct).toFixed(2)}%`;
}

/**
 * Map normalized MarketsData + the tile's GlanceMap → GlanceViewModel.
 *
 * CLIENT-SIDE. Pure. Returns a new object every call (immutable).
 *
 * @param data   normalized markets metrics (from normalize())
 * @param glance validated GlanceMap from the tile config
 */
export function glanceView(data: MarketsData, glance: GlanceMap): GlanceViewModel {
  // --- Determine direction glyph ---
  const dirGlyph = data.direction === 'up'
    ? DIRECTION_GLYPHS.up
    : data.direction === 'down'
      ? DIRECTION_GLYPHS.down
      : DIRECTION_GLYPHS.flat;

  const directionKey: DirectionGlyph = data.direction === 'up'
    ? 'up'
    : data.direction === 'down'
      ? 'down'
      : 'flat';

  // --- primary slot ---
  // Controlled by the `primary` field of the GlanceMap, which matches `display` intent.
  // In practice for markets, primary is always 'price' or 'change' from the config
  // (the glance.primary metric key names a dataShape key). We check which key was
  // configured to decide the format:
  //   glance.primary === 'changePct' or 'change' → show the change/pct
  //   anything else (default: 'price') → show the dollar price
  let primary: string;
  if (glance.primary === 'changePct' || glance.primary === 'change') {
    // display='change' mode: show glyph + pct, e.g. "▲2.3%"
    // max: "▼100.00%" = 9 chars — this would be clamped. Typical: "▲2.30%" = 6 chars.
    primary = `${dirGlyph}${Math.abs(data.changePct).toFixed(2)}%`;
  } else {
    // display='price' mode: show dollar integer, e.g. "$182"
    // max: "$9999" = 5 chars for 4-digit prices — well within 8 chars.
    primary = formatPrimaryPrice(data.price);
  }

  // --- delta slot ---
  let delta: string | null = null;
  if (glance.delta !== null) {
    if (glance.primary === 'changePct' || glance.primary === 'change') {
      // Primary is change → delta is the price: "$182.40"
      delta = formatPrice(data.price);
    } else {
      // Primary is price → delta is change context: "▲$1.23 +0.7%"
      // Keep it short: glyph + abs change + pct
      const absChange = Math.abs(data.change).toFixed(2);
      const pctStr = formatPct(data.changePct);
      delta = `${dirGlyph}$${absChange} ${pctStr}`;
    }
  }

  // --- glance state ---
  let glanceState: GlanceState = 'neutral';
  if (glance.state !== null) {
    glanceState = directionToState(data.direction);
  }

  return {
    dataState: 'ok',
    glanceState,
    slots: {
      primary,
      delta,
      direction: directionKey,
    },
    label: null, // label comes from the tile config, not the adapter
    fetchedAt: null, // fetchedAt comes from the cache envelope
  };
}
